import { isValidRfc } from '@/common/utils/rfc.utils'; // Import new RFC validation util
import { uniqueRfcClientService } from '@/modules/clients/services/clients.service'; // Import client RFC service
import { uniqueRfcService } from '@/modules/enterprise/services/enterprise.service.ts'; // Import RFC service
import {
  validateDescriptionUniqueService,
  validateInternalCodeUniqueService,
} from '@/modules/productsServices/services/productsServices.service';
import { validateTaxGroupNameService } from '@/modules/taxesGroups/services/taxesGroups.service';
import {
  existingUserEmailService,
  uniqueUserEmailService,
} from '@/modules/user/services/user.service';
import axios from 'axios';
import { debounce } from 'lodash'; // Import debounce
import * as yup from 'yup';

declare module 'yup' {
  interface StringSchema {
    uniqueUserEmail(message?: string, debounceDelay?: number): StringSchema;
    uniqueRfc(message?: string, debounceDelay?: number): StringSchema;
    uniqueRfcClient(message?: string, debounceDelay?: number, clientId?: number): StringSchema;
    existingUserEmail(message?: string, debounceDelay?: number): StringSchema;
    uniqueTaxGroupName(
      message?: string,
      debounceDelay?: number,
      enterpriseId?: number,
      taxGroupId?: number,
    ): StringSchema;
    uniqueInternalCode(
      message?: string,
      debounceDelay?: number,
      productServiceId?: number,
    ): StringSchema;
    uniqueDescription(
      message?: string,
      debounceDelay?: number,
      productServiceId?: number,
    ): StringSchema;
    // Example for a new debounced validation, can be added as needed
    // uniqueDescription(message?: string, debounceDelay?: number): StringSchema;
  }
}

interface CacheEntry {
  isValid: boolean;
  timestamp: number;
}

const CACHE_DURATION = 1 * 60 * 1000; // 1 minute

const getCachedResult = (cache: Map<string, CacheEntry>, key: string): boolean | null => {
  const cached = cache.get(key);
  if (!cached) return null;
  if (Date.now() - cached.timestamp > CACHE_DURATION) {
    cache.delete(key);
    return null;
  }
  return cached.isValid;
};

const setCachedResult = (cache: Map<string, CacheEntry>, key: string, isValid: boolean): void => {
  cache.set(key, { isValid, timestamp: Date.now() });
};

const DEFAULT_DEBOUNCE_DELAY = 500;

// Generic asynchronous validation handler
function performAsyncValidation(
  value: string | undefined,
  context: yup.TestContext,
  config: {
    service: (val: string, signal?: AbortSignal) => Promise<boolean>;
    cache: Map<string, CacheEntry>;
    controllerRef: { current: AbortController | null };
    defaultMessage: string;
    validationName: string;
    preValidationCheck?: (val: string) => boolean;
    debounceDelay?: number;
    cacheKey?: string;
  },
): Promise<true | yup.ValidationError> {
  const { path, createError } = context;
  const {
    service,
    cache,
    controllerRef,
    defaultMessage,
    validationName,
    preValidationCheck,
    debounceDelay = DEFAULT_DEBOUNCE_DELAY,
  } = config;

  return new Promise(resolve => {
    // If there was a previous controller for this validation type, abort it immediately
    // so pending debounces or requests from previous inputs don't complete
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }

    if (!value) {
      resolve(true);
      return;
    }

    if (preValidationCheck && !preValidationCheck(value)) {
      resolve(true); // Pre-check failed, consider it a pass for *this* rule, another rule handles format
      return;
    }

    // Create a new AbortController for this specific validation attempt.
    const currentAttemptController = new AbortController();
    const signal = currentAttemptController.signal;

    // Store the new controller as the current one for this validation type.
    controllerRef.current = currentAttemptController;

    const executeValidationLogic = async () => {
      // Check if this attempt was aborted before execution (e.g., by a very fast subsequent validation)
      if (signal.aborted) {
        resolve(true);
        return;
      }

      const cacheKeyToUse = config.cacheKey ?? value;
      const cachedResult = getCachedResult(cache, cacheKeyToUse); // value is already confirmed to be non-empty
      if (cachedResult !== null) {
        controllerRef.current = null; // Clear controller as we are done with this path
        resolve(cachedResult ? true : createError({ path, message: defaultMessage }));
        return;
      }

      try {
        const isValid = await service(value, signal);
        setCachedResult(cache, cacheKeyToUse, isValid);
        controllerRef.current = null; // API call finished
        resolve(isValid ? true : createError({ path, message: defaultMessage }));
      } catch (error: unknown) {
        controllerRef.current = null; // API call finished or aborted
        if (axios.isCancel(error) || (error instanceof Error && error.name === 'AbortError')) {
          resolve(true); // Request was intentionally cancelled or aborted
        } else {
          // Log other errors for debugging
          console.error(`[${validationName}] API Error:`, error);
          resolve(
            createError({
              path,
              message: `Error al validar ${validationName.toLowerCase()}. Intente de nuevo.`,
            }),
          );
        }
      }
    };

    if (debounceDelay > 0) {
      const debouncedExecution = debounce(() => {
        // Re-check signal before executing, as it might have been aborted during the debounce wait
        if (signal.aborted) {
          resolve(true);
          return;
        }
        executeValidationLogic();
      }, debounceDelay);
      debouncedExecution();
    } else {
      executeValidationLogic();
    }
  });
}

// Specific caches and controllers
const emailValidationCache = new Map<string, CacheEntry>();
const emailControllerRef = { current: null as AbortController | null };

const rfcValidationCache = new Map<string, CacheEntry>();
const rfcControllerRef = { current: null as AbortController | null };

const clientRfcValidationCache = new Map<string, CacheEntry>();
const clientRfcControllerRef = { current: null as AbortController | null };

yup.addMethod<yup.StringSchema>(
  yup.string,
  'uniqueUserEmail',
  function (message = 'Este email ya se encuentra registrado', debounceDelay?: number) {
    return this.test('uniqueUserEmail', message, function (value) {
      return performAsyncValidation(value, this, {
        service: uniqueUserEmailService,
        cache: emailValidationCache,
        controllerRef: emailControllerRef,
        defaultMessage: message,
        validationName: 'Email',
        preValidationCheck: val => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val),
        debounceDelay: debounceDelay,
      });
    });
  },
);

yup.addMethod<yup.StringSchema>(
  yup.string,
  'existingUserEmail',
  function (message = 'Este email no se encuentra registrado', debounceDelay?: number) {
    return this.test('existingUserEmail', message, function (value) {
      return performAsyncValidation(value, this, {
        service: existingUserEmailService,
        cache: emailValidationCache,
        controllerRef: emailControllerRef,
        defaultMessage: message,
        validationName: 'Email',
        preValidationCheck: val => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val),
        debounceDelay: debounceDelay,
      });
    });
  },
);

yup.addMethod<yup.StringSchema>(
  yup.string,
  'uniqueRfc',
  function (message = 'Este RFC ya se encuentra registrado', debounceDelay?: number) {
    return this.test('uniqueRfc', message, function (value) {
      return performAsyncValidation(value, this, {
        service: uniqueRfcService,
        cache: rfcValidationCache,
        controllerRef: rfcControllerRef,
        defaultMessage: message,
        validationName: 'RFC',
        preValidationCheck: val => isValidRfc(val),
        debounceDelay: debounceDelay,
      });
    });
  },
);

yup.addMethod<yup.StringSchema>(
  yup.string,
  'uniqueRfcClient',
  function (message = 'Este RFC ya fue registrado', debounceDelay?: number, clientId?: number) {
    return this.test('uniqueRfcClient', message, function (value) {
      return performAsyncValidation(value, this, {
        service: (val: string, signal?: AbortSignal) =>
          uniqueRfcClientService(val, signal, clientId),
        cache: clientRfcValidationCache,
        controllerRef: clientRfcControllerRef,
        defaultMessage: message,
        validationName: 'RFC Cliente',
        preValidationCheck: val => isValidRfc(val),
        debounceDelay: debounceDelay,
        cacheKey: `${value}-${clientId || 'new'}`,
      });
    });
  },
);

// Tax Group Name validation
const taxGroupNameValidationCache = new Map<string, CacheEntry>();
const taxGroupNameControllerRef = { current: null as AbortController | null };

const uniqueTaxGroupNameService = async (
  name: string,
  signal?: AbortSignal,
  enterpriseId?: number,
  taxGroupId?: number,
): Promise<boolean> => {
  if (!enterpriseId) return true;

  try {
    const result = await validateTaxGroupNameService(name, enterpriseId, taxGroupId);
    return result.is_unique;
  } catch (error) {
    console.error('Error validating tax group name:', error);
    return false;
  }
};

yup.addMethod<yup.StringSchema>(
  yup.string,
  'uniqueTaxGroupName',
  function (
    message = 'Ya existe un grupo de impuestos con este nombre',
    debounceDelay?: number,
    enterpriseId?: number,
    taxGroupId?: number,
  ) {
    return this.test('uniqueTaxGroupName', message, function (value) {
      return performAsyncValidation(value, this, {
        service: (val: string, signal?: AbortSignal) =>
          uniqueTaxGroupNameService(val, signal, enterpriseId, taxGroupId),
        cache: taxGroupNameValidationCache,
        controllerRef: taxGroupNameControllerRef,
        defaultMessage: message,
        validationName: 'Nombre del Grupo de Impuestos',
        debounceDelay: debounceDelay,
        cacheKey: `${value}-${enterpriseId || 'no-ent'}-${taxGroupId || 'new'}`,
      });
    });
  },
);

// Product Service Internal Code validation
const internalCodeValidationCache = new Map<string, CacheEntry>();
const internalCodeControllerRef = { current: null as AbortController | null };

yup.addMethod<yup.StringSchema>(
  yup.string,
  'uniqueInternalCode',
  function (
    message = 'Este código interno ya se encuentra registrado',
    debounceDelay?: number,
    productServiceId?: number,
  ) {
    return this.test('uniqueInternalCode', message, function (value) {
      return performAsyncValidation(value, this, {
        service: (val: string, signal?: AbortSignal) =>
          validateInternalCodeUniqueService(val, signal, productServiceId),
        cache: internalCodeValidationCache,
        controllerRef: internalCodeControllerRef,
        defaultMessage: message,
        validationName: 'Código Interno',
        debounceDelay: debounceDelay,
        cacheKey: `${value}-${productServiceId || 'new'}`,
      });
    });
  },
);

// Product Service Description validation
const descriptionValidationCache = new Map<string, CacheEntry>();
const descriptionControllerRef = { current: null as AbortController | null };

yup.addMethod<yup.StringSchema>(
  yup.string,
  'uniqueDescription',
  function (
    message = 'Esta descripción ya se encuentra registrada',
    debounceDelay?: number,
    productServiceId?: number,
  ) {
    return this.test('uniqueDescription', message, function (value) {
      return performAsyncValidation(value, this, {
        service: (val: string, signal?: AbortSignal) =>
          validateDescriptionUniqueService(val, signal, productServiceId),
        cache: descriptionValidationCache,
        controllerRef: descriptionControllerRef,
        defaultMessage: message,
        validationName: 'Descripción',
        debounceDelay: debounceDelay,
        cacheKey: `${value}-${productServiceId || 'new'}`,
      });
    });
  },
);
/**
// HOW TO USE FOR A NEW FIELD LIKE DESCRIPTION (example):
// 1. Add to Yup's StringSchema declaration:
//    uniqueDescription(message?: string, debounceDelay?: number): StringSchema;
// 2. Create cache and controllerRef:
//    const descriptionCache = new Map<string, CacheEntry>();
//    const descriptionControllerRef = { current: null as AbortController | null };
// 3. Implement the service (e.g., uniqueDescriptionService) that accepts an AbortSignal.
// 4. Add the Yup method:
/** */
